import { ScopedKey, ServerScope, type ServerScope as ServerScopeType } from "@/utils/server-scope"

const normalize = (directory: string) => directory.replace(/[\\/]+$/, "")
const key = (scope: ServerScopeType, directory: string) => ScopedKey.from(scope, normalize(directory))

type State =
  | {
      status: "pending"
    }
  | {
      status: "ready"
    }
  | {
      status: "failed"
      message: string
    }

const state = new Map<string, State>()
const waiters = new Map<
  string,
  {
    promise: Promise<State>
    resolve: (state: State) => void
  }
>()

function deferred() {
  const box = { resolve: (_: State) => {} }
  const promise = new Promise<State>((resolve) => {
    box.resolve = resolve
  })
  return { promise, resolve: box.resolve }
}

function scopedDirectory(scopeOrDirectory: ServerScopeType | string, directory?: string) {
  if (directory === undefined) return { scope: ServerScope.local, directory: scopeOrDirectory }
  return { scope: scopeOrDirectory as ServerScopeType, directory }
}

function get(directory: string): State | undefined
function get(scope: ServerScopeType, directory: string): State | undefined
function get(scopeOrDirectory: ServerScopeType | string, directory?: string) {
  const input = scopedDirectory(scopeOrDirectory, directory)
  return state.get(key(input.scope, input.directory))
}

function pending(directory: string): void
function pending(scope: ServerScopeType, directory: string): void
function pending(scopeOrDirectory: ServerScopeType | string, directory?: string) {
  const input = scopedDirectory(scopeOrDirectory, directory)
  const id = key(input.scope, input.directory)
  const current = state.get(id)
  if (current && current.status !== "pending") return
  state.set(id, { status: "pending" })
}

function ready(directory: string): void
function ready(scope: ServerScopeType, directory: string): void
function ready(scopeOrDirectory: ServerScopeType | string, directory?: string) {
  const input = scopedDirectory(scopeOrDirectory, directory)
  const id = key(input.scope, input.directory)
  const next = { status: "ready" } as const
  state.set(id, next)
  const waiter = waiters.get(id)
  if (!waiter) return
  waiters.delete(id)
  waiter.resolve(next)
}

function failed(directory: string, message: string): void
function failed(scope: ServerScopeType, directory: string, message: string): void
function failed(scopeOrDirectory: ServerScopeType | string, directoryOrMessage: string, message?: string) {
  const input =
    message === undefined
      ? { scope: ServerScope.local, directory: scopeOrDirectory, message: directoryOrMessage }
      : { scope: scopeOrDirectory as ServerScopeType, directory: directoryOrMessage, message }
  const id = key(input.scope, input.directory)
  const next = { status: "failed", message: input.message } as const
  state.set(id, next)
  const waiter = waiters.get(id)
  if (!waiter) return
  waiters.delete(id)
  waiter.resolve(next)
}

function wait(directory: string): Promise<State>
function wait(scope: ServerScopeType, directory: string): Promise<State>
function wait(scopeOrDirectory: ServerScopeType | string, directory?: string) {
  const input = scopedDirectory(scopeOrDirectory, directory)
  const id = key(input.scope, input.directory)
  const current = state.get(id)
  if (current && current.status !== "pending") return Promise.resolve(current)

  const existing = waiters.get(id)
  if (existing) return existing.promise

  const waiter = deferred()

  waiters.set(id, waiter)
  return waiter.promise
}

export const Worktree = {
  get,
  pending,
  ready,
  failed,
  wait,
}
